import { create } from 'zustand';
import { apiService } from '../services/api';

interface User {
  id: string;
  nickname: string;
  email?: string;
  phone?: string;
  bio?: string;
  avatar_url?: string;
  role: string;
  has_password?: boolean;
  wechat_openid?: string | null;
}

type LoadingState = 'idle' | 'loading' | 'success' | 'error';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  loginState: LoadingState;
  registerState: LoadingState;
  verificationState: LoadingState;
  resetPasswordState: LoadingState;

  setTokens: (accessToken: string, refreshToken: string) => void;
  login: (data: { contact: string; password: string }) => Promise<void>;
  loginWithCode: (data: { contact: string; verificationCode: string }) => Promise<void>;
  register: (data: { password: string; nickname?: string; verificationCode: string; email?: string; referralCode?: string }) => Promise<void>;
  sendVerificationCode: (data: { contact?: string; phone?: string; email?: string; type: string }) => Promise<void>;
  resetPassword: (data: { verificationCode: string; newPassword: string; phone?: string; email?: string }) => Promise<void>;
  getCurrentUser: () => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  loginState: 'idle',
  registerState: 'idle',
  verificationState: 'idle',
  resetPasswordState: 'idle',

  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    set({ accessToken, refreshToken });
  },

  login: async (data) => {
    set({ loginState: 'loading' });
    try {
      const res: any = await apiService.post('/auth/login', data);
      if (res.success) {
        const { user, tokens } = res.data;
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        set({ user, accessToken: tokens.accessToken, refreshToken: tokens.refreshToken, loginState: 'success' });
      } else {
        throw new Error(res.message);
      }
    } catch (error: any) {
      set({ loginState: 'error' });
      throw new Error(error.message || 'Login failed');
    }
  },

  loginWithCode: async (data) => {
    set({ loginState: 'loading' });
    try {
      const res: any = await apiService.post('/auth/login-with-code', data);
      if (res.success) {
        const { user, tokens } = res.data;
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        set({ user, accessToken: tokens.accessToken, refreshToken: tokens.refreshToken, loginState: 'success' });
      } else {
        throw new Error(res.message);
      }
    } catch (error: any) {
      set({ loginState: 'error' });
      throw new Error(error.message || 'Code sign-in failed');
    }
  },

  register: async (data) => {
    set({ registerState: 'loading' });
    try {
      const res: any = await apiService.post('/auth/register', data);
      if (res.success) {
        const { user, tokens } = res.data;
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        set({ user, accessToken: tokens.accessToken, refreshToken: tokens.refreshToken, registerState: 'success' });
      } else {
        throw new Error(res.message);
      }
    } catch (error: any) {
      set({ registerState: 'error' });
      throw new Error(error.message || 'Registration failed');
    }
  },

  sendVerificationCode: async (data) => {
    set({ verificationState: 'loading' });
    try {
      const res: any = await apiService.post('/auth/send-verification-code', data);
      if (res.success) {
        set({ verificationState: 'success' });
      } else {
        throw new Error(res.message);
      }
    } catch (error: any) {
      set({ verificationState: 'error' });
      throw new Error(error.message || 'Failed to send code');
    }
  },

  resetPassword: async (data) => {
    set({ resetPasswordState: 'loading' });
    try {
      const res: any = await apiService.post('/auth/reset-password', data);
      if (res.success) {
        set({ resetPasswordState: 'success' });
      } else {
        throw new Error(res.message);
      }
    } catch (error: any) {
      set({ resetPasswordState: 'error' });
      throw new Error(error.message || 'Failed to reset password');
    }
  },

  getCurrentUser: async () => {
    try {
      const res: any = await apiService.get('/auth/profile');
      if (res.success) {
        set({ user: res.data.user });
      }
    } catch {
      // ignore
    }
  },

  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('support_contacted'); // 登出清除客服未读轮询标志
    set({ user: null, accessToken: null, refreshToken: null, loginState: 'idle' });
    window.location.href = '/login';
  },
}));
